import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

/**
 * Portal bridges for the topbar's two route-owned slots: LEADING (top-left,
 * beside the brand — page title / model picker) and ACTIONS (top-right — the
 * page's primary controls).
 *
 * The global Topbar lives in the AppShell, ABOVE each route's providers (e.g.
 * chat's `ChatRunProvider`). A feature route that owns state — like the chat
 * model picker or a gallery's search field — can't render into the Topbar
 * directly without lifting that state. Instead the Topbar exposes DOM slots
 * here and the route portals its controls into them: the portaled node keeps
 * the route's React context (portals move DOM, not the tree), so `useChatRun()`
 * etc. still resolve. Only one route mounts at a time, so each slot has a
 * single owner per screen.
 */
interface TopbarSlot {
  el: HTMLElement | null;
  register: (el: HTMLElement | null) => void;
}

interface TopbarSlotValue {
  leading: TopbarSlot;
  actions: TopbarSlot;
}

const TopbarSlotContext = createContext<TopbarSlotValue | null>(null);

export function TopbarSlotProvider({ children }: { children: ReactNode }) {
  const [leadingEl, setLeadingEl] = useState<HTMLElement | null>(null);
  const [actionsEl, setActionsEl] = useState<HTMLElement | null>(null);
  const registerLeading = useCallback((node: HTMLElement | null) => setLeadingEl(node), []);
  const registerActions = useCallback((node: HTMLElement | null) => setActionsEl(node), []);
  const value = useMemo<TopbarSlotValue>(
    () => ({
      leading: { el: leadingEl, register: registerLeading },
      actions: { el: actionsEl, register: registerActions },
    }),
    [leadingEl, actionsEl, registerLeading, registerActions],
  );
  return <TopbarSlotContext.Provider value={value}>{children}</TopbarSlotContext.Provider>;
}

const noopRegister = (): void => {};

/** The Topbar attaches its leading container via this ref callback. */
export function useTopbarLeadingRef(): (el: HTMLElement | null) => void {
  return useContext(TopbarSlotContext)?.leading.register ?? noopRegister;
}

/** The Topbar attaches its actions (right-side) container via this ref callback. */
export function useTopbarActionsRef(): (el: HTMLElement | null) => void {
  return useContext(TopbarSlotContext)?.actions.register ?? noopRegister;
}

/** Feature routes render their leading control (title, picker) through this portal. */
export function TopbarLeading({ children }: { children: ReactNode }) {
  const el = useContext(TopbarSlotContext)?.leading.el ?? null;
  return el ? createPortal(children, el) : null;
}

/** Feature routes render their primary right-side controls through this portal. */
export function TopbarActions({ children }: { children: ReactNode }) {
  const el = useContext(TopbarSlotContext)?.actions.el ?? null;
  return el ? createPortal(children, el) : null;
}
